import { FormEvent, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import {
  passkeysSupported,
  serializeAssertion,
  toAuthenticationArgs,
  type TwoFactorChallengeInfo,
} from "./two-factor";

const METHOD_LABELS: Record<string, string> = {
  webauthn: "Passkey",
  totp: "Authenticator app",
  email: "Email code",
  recovery: "Recovery code",
};

function methodLabel(method: string): string {
  return METHOD_LABELS[method] ?? method;
}

export function TwoFactorChallenge({
  challenge,
  onDone,
  onRestart,
}: {
  challenge: TwoFactorChallengeInfo;
  onDone: () => void;
  onRestart: () => void;
}) {
  const { refresh } = useAuth();
  const methods = useMemo(
    () => challenge.methods.filter((m) => m !== "recovery"),
    [challenge.methods],
  );
  const canRecover = challenge.methods.includes("recovery");
  const [method, setMethod] = useState<string>(
    challenge.methods.includes("webauthn") && passkeysSupported()
      ? "webauthn"
      : (methods[0] ?? "totp"),
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    setCode("");
    setError(null);
  }, [method]);

  // Auto-send the email code when the email method is shown first.
  useEffect(() => {
    if (method === "email" && !emailSent && !emailBusy) {
      void sendEmailCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  async function sendEmailCode() {
    setEmailBusy(true);
    setError(null);
    try {
      await api("/api/auth/2fa/challenge/email/send", {
        method: "POST",
        body: JSON.stringify({ challengeId: challenge.challengeId }),
      });
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send code.");
    } finally {
      setEmailBusy(false);
    }
  }

  async function verifyWithCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/2fa/challenge/verify", {
        method: "POST",
        body: JSON.stringify({
          challengeId: challenge.challengeId,
          method,
          code,
        }),
      });
      await refresh();
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === "TOO_MANY_2FA_ATTEMPTS"
          ? "Too many attempts. Sign in again from the start."
          : err instanceof Error
            ? err.message
            : "Verification failed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function verifyWithPasskey() {
    if (!passkeysSupported()) {
      setError("Passkeys are not supported in this browser.");
      return;
    }
    setPasskeyBusy(true);
    setError(null);
    try {
      const { options } = await api<{
        options: {
          challenge: string;
          rpId: string;
          timeout?: number;
          userVerification?: UserVerificationRequirement;
          allowCredentials?: { type: string; id: string }[];
        };
      }>("/api/auth/2fa/challenge/webauthn/start", {
        method: "POST",
        body: JSON.stringify({ challengeId: challenge.challengeId }),
      });
      const credential = await navigator.credentials.get(
        toAuthenticationArgs(options),
      );
      if (!credential) throw new Error("Passkey was cancelled.");
      await api("/api/auth/2fa/challenge/verify", {
        method: "POST",
        body: JSON.stringify({
          challengeId: challenge.challengeId,
          method: "webauthn",
          code: serializeAssertion(credential),
        }),
      });
      await refresh();
      onDone();
    } catch (err) {
      setError(
        err instanceof DOMException && err.name === "NotAllowedError"
          ? "Passkey was cancelled or timed out."
          : err instanceof Error
            ? err.message
            : "Passkey verification failed.",
      );
    } finally {
      setPasskeyBusy(false);
    }
  }

  const codeLabel =
    method === "recovery"
      ? "Recovery code (XXXX-XXXX)"
      : method === "email"
        ? "6-digit email code"
        : "6-digit authenticator code";

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-rs-ink">
          Two-factor verification
        </h2>
        <p className="text-sm text-rs-ink-muted">
          This account has two-factor authentication enabled. Verify with one of
          your enrolled methods.
        </p>
      </div>

      {methods.length > 1 ? (
        <div
          className="flex flex-wrap gap-2"
          role="tablist"
          aria-label="Second-factor method"
        >
          {methods.map((m) => (
            <Button
              key={m}
              type="button"
              size="sm"
              variant={method === m ? "primary" : "secondary"}
              onClick={() => setMethod(m)}
            >
              {methodLabel(m)}
            </Button>
          ))}
        </div>
      ) : null}

      {method === "webauthn" ? (
        <div className="space-y-3">
          <Button
            type="button"
            className="rs-auth-submit rs-auth-submit-block"
            disabled={passkeyBusy}
            onClick={() => void verifyWithPasskey()}
          >
            {passkeyBusy ? "Waiting for passkey…" : "Use passkey"}
          </Button>
          <p className="text-xs text-rs-ink-muted">
            You’ll be asked for your fingerprint, face, or security key.
          </p>
        </div>
      ) : (
        <form className="space-y-3" onSubmit={verifyWithCode}>
          {method === "email" ? (
            <p className="text-xs text-rs-ink-muted">
              {emailSent
                ? "We sent a code to your email. It expires in 10 minutes."
                : emailBusy
                  ? "Sending a code to your email…"
                  : "Request a code to continue."}{" "}
              <button
                type="button"
                className="font-medium underline-offset-2 hover:underline"
                disabled={emailBusy}
                onClick={() => void sendEmailCode()}
              >
                {emailBusy ? "Sending…" : "Resend code"}
              </button>
            </p>
          ) : null}
          <Input
            id="two-factor-code"
            name="code"
            autoComplete="one-time-code"
            required
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={codeLabel}
            aria-label={codeLabel}
            inputMode={method === "recovery" ? "text" : "numeric"}
          />
          <Button
            className="rs-auth-submit rs-auth-submit-block"
            type="submit"
            disabled={busy}
          >
            {busy ? "Verifying…" : "Verify"}
          </Button>
        </form>
      )}

      {error ? (
        <p className="text-sm text-rs-danger" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-1 text-sm">
        {canRecover && method !== "recovery" ? (
          <button
            type="button"
            className="text-rs-ink-muted underline-offset-2 hover:underline"
            onClick={() => setMethod("recovery")}
          >
            Lost your device? Use a recovery code
          </button>
        ) : (
          <span />
        )}
        <button
          type="button"
          className="text-rs-ink-muted underline-offset-2 hover:underline"
          onClick={onRestart}
        >
          Back to sign in
        </button>
      </div>
    </div>
  );
}
